import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useWikiV2Client, wikiErrorCode } from '../../../hooks/useWikiV2';
import WikiIcon from '../shared/WikiIcon';
import { WikiLoading } from '../shared/WikiStates';
import { invalidateWiki, useWikiQuery, wikiKeys } from '../shared/useWikiQueries';
import { wikiPaths } from '../shared/wikiRoutes';
import { wikiUi } from '../shared/wikiUi';
import {
    PAGE_COPY,
    ackToast,
    ackVersion,
    ackedNote,
    compareHeading,
    needAckNote,
    suggestMeta,
    wikiErrorMessage,
} from './wikiPageCopy';

const enc = encodeURIComponent;

/** Draft banner (prototype §1.3 c) — `status === 'draft'`; Continue editing for editors. */
export function DraftBanner({ canEdit, onEdit }) {
    return (
        <div className="wk-pg-banner wk-pg-banner--draft" data-testid="wiki-page-draft-banner">
            <WikiIcon name="file-text" size={17} />
            <p className="wk-pg-banner-text">{PAGE_COPY.draftBanner}</p>
            {canEdit && (
                <button type="button" className="wk-btn wk-btn--primary wk-btn--sm" onClick={onEdit}>
                    {PAGE_COPY.continueEditing}
                </button>
            )}
        </div>
    );
}

/** A published page with unpublished changes (writers only; production addition). */
export function PendingDraftBanner({ onEdit }) {
    return (
        <div className="wk-pg-banner wk-pg-banner--draft" data-testid="wiki-page-pending-draft">
            <WikiIcon name="pencil" size={16} />
            <p className="wk-pg-banner-text">{PAGE_COPY.pendingDraftBanner}</p>
            <button type="button" className="wk-btn wk-btn--primary wk-btn--sm" onClick={onEdit}>
                {PAGE_COPY.continueEditing}
            </button>
        </div>
    );
}

/**
 * Needs-acknowledgement banner (§1.3 d) for every role; on success the banner
 * swaps to the acknowledged one (§1.3 e), the read count moves, and the nav
 * badge / Home / To acknowledge / list pills refresh.
 */
export function AckBanner({ page }) {
    const client = useWikiV2Client();
    const queryClient = useQueryClient();
    const v = ackVersion(page);
    const ack = useMutation({
        mutationFn: () => client.post(`/wiki/pages/${enc(page.page_id)}/ack`, { version: v }),
        onSuccess: (res) => {
            queryClient.setQueryData(wikiKeys.page(page.page_id), (old) => (old ? {
                ...old,
                acknowledged: true,
                read_by: typeof res?.read_by === 'number' ? res.read_by : old.read_by,
                read_of: typeof res?.read_of === 'number' ? res.read_of : old.read_of,
            } : old));
            wikiUi.toast(ackToast(page.title, v));
            // Nav To-acknowledge count, Home panel, To acknowledge view, list
            // pills and this page all move together.
            invalidateWiki(queryClient, 'ack', 'home', 'counts', 'pages', ['page', page.page_id]);
        },
        onError: (error) => {
            wikiUi.toast(wikiErrorMessage(error, "Couldn't save your confirmation. Please try again."));
            if (wikiErrorCode(error) === 'ack_version_conflict') invalidateWiki(queryClient, ['page', page.page_id]);
        },
    });

    if (page.acknowledged) {
        return (
            <div className="wk-pg-banner wk-pg-banner--acked" data-testid="wiki-page-acked">
                <WikiIcon name="circle-check" size={16} />
                <p className="wk-pg-banner-text">{ackedNote(v)}</p>
            </div>
        );
    }
    return (
        <div className="wk-pg-banner wk-pg-banner--ack" data-testid="wiki-page-needs-ack">
            <WikiIcon name="badge-check" size={18} />
            <p className="wk-pg-banner-text">{needAckNote(v)}</p>
            <button
                type="button"
                className="wk-btn wk-btn--primary wk-btn--sm"
                data-testid="wiki-page-ack-btn"
                disabled={ack.isPending}
                onClick={() => ack.mutate()}
            >
                {PAGE_COPY.ackButton}
            </button>
        </div>
    );
}

/**
 * Suggested-edit card (§1.3 f). The API returns open suggestions to the page
 * owner and admins only, so the card is only fetched for them. Dismiss: every
 * reviewer. Accept: page editors — published straight away as v{n+1} (the
 * prototype's "Saved as a new version"), or, when the page already has
 * unpublished edits, applied into that draft and the editor opened.
 */
export function SuggestedEdits({ page, canEdit }) {
    const client = useWikiV2Client();
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const pageId = page.page_id;
    const key = [...wikiKeys.page(pageId), 'suggested-edits'];
    const query = useWikiQuery(key, `/wiki/pages/${enc(pageId)}/suggested-edits`);
    const accept = useMutation({
        mutationFn: ({ edit, publish }) => client.post(
            `/wiki/pages/${enc(pageId)}/suggested-edits/${enc(edit.edit_id)}/accept`,
            publish ? { publish: true } : {},
        ),
        onSuccess: (res, { publish }) => {
            const published = res && typeof res.published === 'boolean' ? res.published : publish;
            wikiUi.toast(published ? PAGE_COPY.suggestAcceptToast : PAGE_COPY.suggestAcceptDraftToast);
            invalidateWiki(queryClient, ['page', pageId], 'pages', 'home', 'ack', 'counts');
            if (!published) navigate(wikiPaths.pageEdit(pageId));
        },
        onError: (error) => {
            wikiUi.toast(wikiErrorMessage(error, "Couldn't apply the suggestion. Please try again."));
            invalidateWiki(queryClient, ['page', pageId]);
        },
    });
    const dismiss = useMutation({
        mutationFn: (edit) => client.post(`/wiki/pages/${enc(pageId)}/suggested-edits/${enc(edit.edit_id)}/dismiss`, {}),
        onSuccess: (_res, edit) => {
            queryClient.setQueryData(key, (old) => (old && Array.isArray(old.edits)
                ? { ...old, edits: old.edits.filter((row) => row.edit_id !== edit.edit_id) }
                : old));
            wikiUi.toast(PAGE_COPY.suggestDismissToast);
        },
        onError: (error) => {
            wikiUi.toast(wikiErrorMessage(error, "Couldn't dismiss the suggestion. Please try again."));
            invalidateWiki(queryClient, key.slice(1));
        },
    });

    const edits = query.data && Array.isArray(query.data.edits) ? query.data.edits : [];
    const edit = edits[0];
    if (!edit) return null;
    const busy = accept.isPending || dismiss.isPending;
    return (
        <section className="wk-pg-sug" aria-labelledby="wk-pg-sug-title" data-testid="wiki-page-suggestion" data-edit-id={edit.edit_id}>
            <div className="wk-pg-sug-head">
                <WikiIcon name="brain" size={16} />
                <span id="wk-pg-sug-title" className="wk-pg-sug-title">{PAGE_COPY.suggestTitle}</span>
                <span className="wk-pg-sug-meta">{suggestMeta(edit.section)}</span>
            </div>
            <p className="wk-pg-sug-body">{PAGE_COPY.suggestBody}</p>
            <div className="wk-pg-sug-cols">
                <div className="wk-pg-sug-box">
                    <span className="wk-t-label">{PAGE_COPY.suggestNow}</span>
                    <p className="wk-pg-sug-text wk-pg-sug-text--now">{edit.current_text}</p>
                </div>
                <div className="wk-pg-sug-box wk-pg-sug-box--new">
                    <span className="wk-t-label">{PAGE_COPY.suggestNew}</span>
                    <p className="wk-pg-sug-text wk-pg-sug-text--new">{edit.suggested_text}</p>
                </div>
            </div>
            <div className="wk-pg-sug-actions">
                <button
                    type="button"
                    className="wk-btn wk-btn--secondary wk-btn--sm"
                    data-testid="wiki-page-suggestion-dismiss"
                    disabled={busy}
                    onClick={() => dismiss.mutate(edit)}
                >
                    {PAGE_COPY.suggestDismiss}
                </button>
                {canEdit && (
                    <button
                        type="button"
                        className="wk-btn wk-btn--indigo wk-btn--sm"
                        data-testid="wiki-page-suggestion-accept"
                        disabled={busy}
                        onClick={() => accept.mutate({ edit, publish: !page.has_draft })}
                    >
                        <WikiIcon name="check" size={13} />
                        {PAGE_COPY.suggestAccept}
                    </button>
                )}
            </div>
        </section>
    );
}

/**
 * Compare card (production addition — the prototype only toasted
 * "Comparing v2 with v4."): the unified diff from `GET /pages/{id}/diff`.
 */
export function CompareCard({ pageId, from, to, onClose }) {
    const query = useWikiQuery(
        [...wikiKeys.page(pageId), 'diff', from, to],
        `/wiki/pages/${enc(pageId)}/diff?a=${from}&b=${to}`,
    );
    const lines = (query.data && Array.isArray(query.data.lines) ? query.data.lines : [])
        .filter((line, index) => !(index < 2 && (line.startsWith('--- ') || line.startsWith('+++ '))));
    return (
        <section className="wk-card wk-pg-compare" aria-labelledby="wk-pg-compare-title" data-testid="wiki-page-compare">
            <div className="wk-pg-diff-head">
                <WikiIcon name="history" size={16} />
                <h2 id="wk-pg-compare-title" className="wk-t-name">{compareHeading(from, to)}</h2>
                <button type="button" className="wk-btn wk-btn--ghost wk-btn--icon" aria-label="Close comparison" onClick={onClose}>
                    <WikiIcon name="x" size={16} />
                </button>
            </div>
            {query.isLoading && <WikiLoading label="Comparing versions…" />}
            {query.isError && <p className="wk-t-meta wk-pg-diff-note">{wikiErrorMessage(query.error, "The comparison didn't load.")}</p>}
            {query.isSuccess && lines.length === 0 && (
                <p className="wk-t-meta wk-pg-diff-note">These versions have the same content.</p>
            )}
            {lines.length > 0 && (
                <pre className="wk-pg-diff" data-testid="wiki-page-diff">
                    {lines.map((line, index) => {
                        let kind = '';
                        if (line.startsWith('@@')) kind = 'hunk';
                        else if (line.startsWith('+')) kind = 'add';
                        else if (line.startsWith('-')) kind = 'del';
                        return (
                            <span key={index} className={`wk-pg-diff-line${kind ? ` wk-pg-diff-line--${kind}` : ''}`}>
                                {line || ' '}
                            </span>
                        );
                    })}
                </pre>
            )}
        </section>
    );
}
